'use client';

import { useTranslations } from 'next-intl';

import { Badge } from '@/components/ui/app-badge';
import { TOAST_IDS } from '@/config/toast-ids';
import { showToast } from '@/lib/ui/toast';

interface CopyableCashtagBadgeProps {
  ticker: string | null | undefined;
  appearance?: 'pill' | 'tag';
}

function toCashtag(ticker: string | null | undefined): string | null {
  if (ticker === null || ticker === undefined || ticker === '') return null;
  return ticker.startsWith('$') ? ticker : `$${ticker}`;
}

/** Neutral cashtag badge that copies the token identifier on click. */
export function CopyableCashtagBadge({
  ticker,
  appearance = 'pill',
}: CopyableCashtagBadgeProps): React.ReactNode | null {
  const t = useTranslations('market.header');
  const cashtag = toCashtag(ticker);

  function handleCopy(): void {
    if (cashtag === null) return;

    void navigator.clipboard.writeText(cashtag).then(() => {
      showToast({
        id: TOAST_IDS.TICKER_COPIED,
        message: t('tickerCopied', { ticker: cashtag }),
        variant: 'success',
      });
    });
  }

  if (cashtag === null) return null;

  return (
    // ALLOW-RAW-BUTTON: copyable inline ticker needs native button semantics without AnimatedButton sizing
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('copyTicker')}
      className={
        appearance === 'tag'
          ? 'rounded-[calc(var(--radius-item)+0.125rem)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-border-primary/50' // ARBITRARY: approved compact market ticker radius is 6px.
          : 'rounded-pill focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-border-primary/50'
      }
    >
      {/* ALLOW-RAW-BADGE-STYLE: exact approved compact ticker treatment. */}
      <Badge
        variant="secondary"
        className={
          appearance === 'tag'
            ? 'rounded-[calc(var(--radius-item)+0.125rem)] border-0 bg-background-tertiary px-1.5 py-0.5 font-mono font-normal leading-5 text-foreground-secondary'
            : 'bg-background-tertiary text-foreground-secondary'
        }
      >
        {cashtag}
      </Badge>
    </button>
  );
}
